import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Search, Command, ArrowRight, Briefcase, Handshake, Scale, PenLine,
  FileText, CalendarClock, Compass, X, Plus, Sparkles, Video, Eye, Calendar,
} from "lucide-react";

const KIND_ICON = {
  matter: Briefcase,
  agreement: Handshake,
  case: Scale,
  draft: PenLine,
  template: FileText,
  obligation: CalendarClock,
  "case-filing-template": Compass,
};

// Static commands the user can dispatch from the palette
const COMMANDS = [
  { id: "cmd-new-matter", label: "New matter", subtitle: "Open a fresh matter", to: "/app/matters", icon: Plus, hint: "M" },
  { id: "cmd-new-case", label: "New case", subtitle: "Start a case workspace", to: "/app/cases/new", icon: Scale, hint: "C" },
  { id: "cmd-claim-explorer", label: "Open Claim Explorer", subtitle: "Map a situation to possible claims", to: "/app/cases/claim-explorer", icon: Compass, hint: "E" },
  { id: "cmd-case-filing", label: "Case filing templates", subtitle: "Jurisdiction-aware causes of action", to: "/app/cases/templates", icon: FileText, hint: "F" },
  { id: "cmd-agreements", label: "Open Agreements", subtitle: "Draft, sign and analyse contracts", to: "/app/agreements", icon: Handshake, hint: "A" },
  { id: "cmd-obligations", label: "Open Obligations", subtitle: "What you owe, when it's due", to: "/app/obligations", icon: CalendarClock, hint: "O" },
  { id: "cmd-calendar", label: "Open Calendar", subtitle: "Hearings, obligations, signing deadlines", to: "/app/calendar", icon: Calendar, hint: "C" },
  { id: "cmd-starters", label: "Open Case Starters", subtitle: "Guided notice + case setup wizards", to: "/app/case-starters", icon: Sparkles, hint: "N" },
  { id: "cmd-sign", label: "Open Signatures", subtitle: "Multi-signer envelopes, audit trail", to: "/app/signatures", icon: PenLine, hint: "S" },
  { id: "cmd-learn", label: "Open Learn", subtitle: "Browse the Marianne Knowledge Base", to: "/app/learn", icon: FileText, hint: "L" },
  { id: "cmd-marketplace", label: "Open Marketplace", subtitle: "Implementation services + lawyers", to: "/app/marketplace", icon: Sparkles, hint: "K" },
  { id: "cmd-nyaya", label: "Nyaya Drishti", subtitle: "Vision-based filing detection", to: "/app/nyaya", icon: Eye },
  { id: "cmd-sentinel", label: "Sentinel", subtitle: "Citizen incident capture", to: "/app/sentinel", icon: Video },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  // ⌘K / Ctrl+K toggle
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ("");
      setHits([]);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  const runSearch = useCallback(async (value) => {
    if (!value.trim()) { setHits([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q: value } });
      setHits(data.hits || []);
      setActiveIdx(0);
    } catch {
      setHits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(q), 180);
    return () => clearTimeout(timerRef.current);
  }, [q, open, runSearch]);

  // Filter static commands client-side by q
  const filteredCommands = q.trim()
    ? COMMANDS.filter((c) => c.label.toLowerCase().includes(q.toLowerCase()) || c.subtitle.toLowerCase().includes(q.toLowerCase()))
    : COMMANDS;

  const combined = [
    ...filteredCommands.map((c) => ({ ...c, kind: "command", title: c.label, subtitle: c.subtitle, href: c.to })),
    ...hits,
  ];

  const go = (item) => {
    setOpen(false);
    navigate(item.href || item.to);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(combined.length - 1, i + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    if (e.key === "Enter" && combined[activeIdx]) go(combined[activeIdx]);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#2A0F5A]/40 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
      onClick={() => setOpen(false)}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-2xl bg-white border border-[#E8E3F0] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E8E3F0]">
          <Search size={16} className="text-[#7B56C9]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search Marianne — matters, agreements, cases, templates, commands…"
            className="flex-1 outline-none text-sm bg-transparent"
            data-testid="command-input"
          />
          {loading && <div className="label-eyebrow !text-[10px]">Searching…</div>}
          <button onClick={() => setOpen(false)} className="text-[#4A5568] hover:text-[#2A0F5A]" data-testid="command-close">
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {combined.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-[#4A5568]" data-testid="command-empty">
              {q.trim() ? "No results. Try a different search." : "Type to search across the workspace."}
            </div>
          ) : (
            <ul>
              {combined.map((item, idx) => {
                const Icon = item.kind === "command" ? item.icon : (KIND_ICON[item.kind] || Search);
                const isActive = idx === activeIdx;
                return (
                  <li
                    key={`${item.kind}-${item.id || item.href}`}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => go(item)}
                    className={`px-5 py-3 flex items-center gap-3 cursor-pointer border-b border-[#E8E3F0]/70 ${isActive ? "bg-[#FAFAF8]" : "bg-white"}`}
                    data-testid={`command-item-${idx}`}
                  >
                    <Icon size={16} className={item.kind === "command" ? "text-[#C5A059]" : "text-[#7B56C9]"} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{item.title || item.label}</div>
                      {item.subtitle && <div className="text-xs text-[#4A5568] truncate">{item.subtitle}</div>}
                    </div>
                    <div className="label-eyebrow !text-[10px] hidden sm:block">{item.kind.replace("-", " ")}</div>
                    {isActive && <ArrowRight size={12} className="text-[#7B56C9]" />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-2.5 border-t border-[#E8E3F0] flex items-center justify-between text-[10px] text-[#4A5568]">
          <div className="flex items-center gap-3">
            <span>↑↓ navigate</span>
            <span>⏎ open</span>
            <span>esc close</span>
          </div>
          <div className="flex items-center gap-1"><Command size={10} /> K</div>
        </div>
      </div>
    </div>
  );
}
